/**
 * Quiet fade-in-and-slide-up reveal for content as it scrolls into view.
 *
 * Progressive enhancement only: elements render normally by default and are
 * only ever hidden once this script has actually attached an observer to
 * them, so a slow/blocked/failed script load never leaves content invisible.
 */

const TARGET_SELECTORS = [
  '.hero__content-wrapper',
  '#shopify-section-trust_bar_Qp7Xz2 .group-block',
  '.collection-card',
  '.product-card',
].join(', ');

const OBSERVER_OPTIONS = {
  // Waits until an element is meaningfully on screen (not just 10% peeking
  // in) before starting the reveal, so the animation is still mid-flight
  // when the rest of the section comes into view instead of finishing early.
  rootMargin: '0px 0px -5% 0px',
  threshold: 0.3,
};

/** @type {IntersectionObserver | null} */
let observer = null;

/** @param {Element} element */
function watch(element) {
  if (element.hasAttribute('data-scroll-reveal')) return;

  element.setAttribute('data-scroll-reveal', '');
  observer?.observe(element);
}

function revealOnIntersect(/** @type {IntersectionObserverEntry[]} */ entries) {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;

    entry.target.setAttribute('data-scroll-revealed', '');
    observer?.unobserve(entry.target);
  }
}

/** Picks up cards added after the initial scan (infinite scroll, filters, pagination). */
function watchForNewCards() {
  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;

        if (node.matches('.collection-card, .product-card')) watch(node);
        node.querySelectorAll?.('.collection-card, .product-card').forEach(watch);
      }
    }
  });

  mutationObserver.observe(document.body, { childList: true, subtree: true });
}

function init() {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion || !('IntersectionObserver' in window)) return;

  const elements = document.querySelectorAll(TARGET_SELECTORS);
  if (!elements.length) return;

  observer = new IntersectionObserver(revealOnIntersect, OBSERVER_OPTIONS);
  elements.forEach(watch);
  watchForNewCards();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
